import {
  DASHBOARD_TEMPLATE_BOARDS,
  type DashboardTemplateKey,
} from "@salestrak/widgets";
import type {
  Color,
  IconName,
  WidgetInstance,
  WidgetRegistry,
} from "@salestrak/ui";
import type { WidgetSeed } from "#app/widgets/dashboards/use-widget-view";

export type DashboardTemplate = {
  key: string;
  label: string;
  description: string;
  icon: IconName;
  color: Color;
  seeds: WidgetSeed[];
};

/**
 * Display copy for each template. The template BOARDS (seeds with their stable
 * `<templateKey>:<index>` widgetKeys already baked) live in
 * @salestrak/widgets — the same module the core API seeds a
 * new team/workflow dashboard's first read from — so a template's board cannot
 * drift from what the server persists. Seed ORDER is identity: never reorder
 * an existing template's seeds, only append (rule enforced at the data).
 */
const PRESENTATION: Record<
  DashboardTemplateKey,
  Pick<DashboardTemplate, "label" | "description" | "icon" | "color">
> = {
  "pipeline-overview": {
    label: "Pipeline overview",
    description: "Track project volume, new work, attention items, and stage movement.",
    icon: "git-branch",
    color: "teal",
  },
  "team-performance": {
    label: "Team performance",
    description: "Compare assignment load, activity, and closed project momentum.",
    icon: "users",
    color: "green",
  },
  "projects-activity": {
    label: "Projects & activity",
    description: "Start with a project table, recent activity feed, and total count.",
    icon: "activity",
    color: "blue",
  },
};

export const DASHBOARD_TEMPLATES: DashboardTemplate[] = DASHBOARD_TEMPLATE_BOARDS.map(
  (board) => ({
    key: board.key,
    seeds: board.seeds,
    ...PRESENTATION[board.key],
  }),
);

/** A template's board as placed instances — the `restoreBoard` payload the
 *  Start-over dialog applies (mode-routed: staged for shared writers,
 *  personal-fork snapshot for personalizing viewers). Local ids only; the
 *  store's diff/commit paths issue real ids on persist. */
export function templateInstances(
  template: DashboardTemplate,
  registry: WidgetRegistry,
): WidgetInstance[] {
  return template.seeds.map((seed, index) => ({
    id: `template-${template.key}:${index}`,
    type: seed.type,
    config: seed.config,
    x: seed.x,
    y: seed.y,
    w: seed.w,
    h: seed.h,
    schemaVersion: registry.get(seed.type)?.schemaVersion ?? 1,
  }));
}
